import { useState, useEffect, useRef } from 'react';
import { useAnalytics } from '@/lib/analytics';
import { useI18n } from '@/lib/i18n';
import { formatSignedUsd } from '@/lib/money-format';
import { safeJsonParse } from './wallet/safe-json';
import { formatDuration } from './wallet/ledger-labels';
import { bucketDateLabel, bucketTipAnchor } from './usage-chart';
import { LedgerCard } from './wallet/LedgerCard';
import './Wallet.scss';

/**
 * The header's own endpoint — see `routes/wallet-usage-summary.ts` for why it is
 * not part of `/api/wallet/status`.
 */
interface UsageSummary {
  windowDays: number;
  spendMicroUsd: number;
  /** Sum over sessions of each session's longest stream. Equals the durations
   *  on the rows below, added up. */
  sessionSeconds: number;
  sessionCount: number;
  /** One bucket per calendar day of the window, oldest first, in micro-USD. */
  dailySpendMicroUsd: number[];
  /** When the first bucket's day began, as a UTC instant. */
  firstDayStartedAt: number;
}

/**
 * The daily spend chart.
 *
 * Bars, not a line: spending is a set of discrete events, and the days with
 * nothing on them are half of what this answers — a line would interpolate
 * across them and invent usage that never happened. Each empty day keeps a
 * hairline so the window reads as continuous rather than as missing data.
 */
function SpendChart({ days, windowDays, firstDayStartedAt }: {
  days: number[];
  windowDays: number;
  firstDayStartedAt: number;
}) {
  const { t, locale } = useI18n();
  const [hovered, setHovered] = useState<number | null>(null);
  const peak = Math.max(...days, 0);

  const tip = hovered === null ? null : {
    ...bucketTipAnchor(hovered, days.length),
    date: bucketDateLabel(firstDayStartedAt, hovered, locale),
    spent: days[hovered],
  };

  return (
    <div className="usage-chart">
      <div className="usage-chart__plot">
        {/* The label lives outside the bar row so a 30-bucket chart needs one
            node rather than thirty, and so it can overhang the bars' height. */}
        {tip && (
          <div
            className={`usage-chart__tip usage-chart__tip--${tip.align}`}
            style={{ left: `${tip.leftPercent}%` }}
            role="status"
          >
            <span className="usage-chart__tip-date">{tip.date}</span>
            <span className="usage-chart__tip-amount">
              {tip.spent > 0 ? formatSignedUsd(tip.spent) : t('dashboard.usage.noSpend')}
            </span>
          </div>
        )}
        <div
          className="usage-chart__bars"
          role="img"
          aria-label={t('dashboard.usage.chartLabel')}
          onMouseLeave={() => setHovered(null)}
        >
          {days.map((value, i) => (
            <span
              key={i}
              className={
                'usage-chart__bar' +
                (value > 0 ? ' usage-chart__bar--spent' : '') +
                (peak > 0 && value === peak ? ' usage-chart__bar--peak' : '') +
                (hovered === i ? ' usage-chart__bar--hovered' : '')
              }
              // Height, not scaleY: a transform would blur the 1px hairline that
              // an empty day is drawn with.
              style={{ height: peak > 0 && value > 0 ? `${Math.max(8, (value / peak) * 100)}%` : '2px' }}
              onMouseEnter={() => setHovered(i)}
            />
          ))}
        </div>
      </div>
      <div className="usage-chart__axis">
        <span>{t('dashboard.usage.chartStart').replace('{{days}}', String(windowDays))}</span>
        <span>{t('dashboard.usage.chartEnd')}</span>
      </div>
    </div>
  );
}

export function Usage() {
  const { trackEvent } = useAnalytics();
  const { t } = useI18n();
  const [summary, setSummary] = useState<UsageSummary | null>(null);
  const fetchAttempted = useRef(false);

  useEffect(() => {
    if (fetchAttempted.current) return;
    fetchAttempted.current = true;
    trackEvent('wallet_page_viewed', { page: 'usage' });
    (async () => {
      try {
        // The viewer's UTC offset, so the server buckets by THEIR calendar days
        // and every bar can be named with a date they recognise. getTimezoneOffset
        // is minutes WEST of UTC, i.e. the sign is inverted from what it labels.
        const tzOffsetMinutes = -new Date().getTimezoneOffset();
        const response = await fetch(
          `/api/wallet/usage-summary?tzOffsetMinutes=${tzOffsetMinutes}`,
          { credentials: 'include' }
        );
        if (!response.ok) return;
        const data = await safeJsonParse<UsageSummary>(response);
        if (data) setSummary(data);
      } catch (err) {
        // A missing header degrades to no header. The ledger below is what this
        // page is for and it fetches independently.
        console.error('Error fetching usage summary:', err);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="wallet-page">
      <div className="wallet-page__header">
        <h1>{t('dashboard.usage.title')}</h1>
        <p>{t('dashboard.usage.subtitle')}</p>
      </div>

      {summary !== null && (
        <div className="usage-summary">
          <div className="usage-summary__lead">
            <span className="usage-summary__label">{t('dashboard.wallet.last30Days')}</span>
            {/* One figure carries the header. The rest are supporting facts in
                one quiet line — a second number of equal weight would put the
                reader back where they started, deciding which one to read. */}
            {/* No minus. A ledger row signs its amount because credits and
                debits share the column; this figure is a spend total under a
                label that already says so, and the sign only adds noise. */}
            <span className="usage-summary__hero">{formatSignedUsd(summary.spendMicroUsd)}</span>
            <span className="usage-summary__facts">
              <b>{formatDuration(summary.sessionSeconds) || '0s'}</b> {t('dashboard.usage.sessionTime')}
              {' · '}
              <b>{summary.sessionCount}</b> {t('dashboard.usage.sessions')}
            </span>
          </div>
          <SpendChart
            days={summary.dailySpendMicroUsd}
            windowDays={summary.windowDays}
            firstDayStartedAt={summary.firstDayStartedAt}
          />
        </div>
      )}

      <div className="wallet-page__grid">
        <LedgerCard kind="usage" />
      </div>
    </div>
  );
}
